import React from 'react';

import { Select } from '@aline/common';
import { AlineButton } from 'src/components';
import { useMobileSheetActionButtonStyles } from 'src/editor/mobile/signing/mobile-sheet-action-button';
import { mobileSheetAutoFocusVar } from 'src/editor/mobile/signing/mobile-sheet-autofocus';
import { mobileFinishSheetOpenVar } from 'src/editor/mobile/signing/MobileFinishSheet';
import { useWalkthroughCta } from 'src/editor/toolbars';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  footer: {
    marginTop: theme.spacing(2),
  },
}));

interface MobileSheetActionFooterProps {
  onDismiss: () => void;
}

/**
 * In-sheet Next/Finish/Done action so the user can advance the walkthrough
 * without first dismissing the sheet.
 */
export function MobileSheetActionFooter({ onDismiss }: MobileSheetActionFooterProps): JSX.Element {
  const { classes } = useStyles();
  const { classes: actionClasses } = useMobileSheetActionButtonStyles();
  const { buttonText, disabled, handleClick, submitting } = useWalkthroughCta({
    finishLabel: 'Finish',
    /**
     * Dismiss the field sheet before opening the confirmation sheet — without
     * this they would briefly stack on top of each other.
     */
    onConfirmFinishNeeded: () => {
      onDismiss();
      mobileFinishSheetOpenVar(true);
    },
  });

  return (
    <div className={classes.footer}>
      <AlineButton
        fullWidth
        buttonClassName={actionClasses.button}
        color="primary"
        data-cy={Select.document.editor.walkthrough.advanceWalkthroughButton}
        /**
         * Block taps while a field save is in flight — see comment in
         * `MobileWalkthroughNextButton.tsx` for the race this prevents.
         */
        disabled={disabled}
        loading={submitting}
        variant="contained"
        onClick={event => {
          /**
           * Pressing Next is a user gesture — auto-focus the next field's
           * input so the user can keep typing without an extra tap.
           */
          mobileSheetAutoFocusVar(true);
          handleClick(event);
        }}
      >
        {buttonText}
      </AlineButton>
    </div>
  );
}
